// all schedule board configuration goes here
// globally available for use in order flows, and to prevent resetting the board when navigating away
import {
  createContext,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useState,
} from 'react';
import { type OrderTechniciansModalType } from 'pos-client/components/technicians/OrderTechniciansModal.tsx';
import { datetimeAtTimezone } from 'pos-client/utils/dateHelpers.ts';
import type { ConflictLaneItemModel } from 'pos-shared/models/conflictLanesSchema';
import type { LocationModel } from 'pos-shared/models/locationSchema.ts';
import { serviceTypeEnum } from 'pos-shared/models/resourceManagementSchema.ts';
import type {
  ScheduleBoardJobModel,
  ScheduleBoardJobsAndBlockedSlotsListModel,
  SelectedOrderDetailsForAssignTechnicianModel,
  ServiceTypesRequestModel,
  TradingTimesModel,
} from 'pos-shared/models/schedulingSchema.ts';

type JobDetailsModalActiveKey = '1' | '2' | '3' | '4' | '5';

export type ScheduleBoardContextProps = {
  selectedLocation: LocationModel | null;
  selectedDate: Date;
  laneConflicts: ConflictLaneItemModel[] | null;
  boardData: ScheduleBoardJobsAndBlockedSlotsListModel;
  searchTerm: string;
  serviceTypeFilter: ServiceTypesRequestModel;
  jobDetailsModalData: ScheduleBoardJobModel | null;
  startDayHour: number;
  endDayHour: number;
  tradingTimes: TradingTimesModel;
  isNonTradingDate: boolean;
  selectedTimeSlot: Date | null;
  selectedLaneId: string | null;
  editTechniciansModal: OrderTechniciansModalType;
  selectedOrderDetailsForAssignTechnician: SelectedOrderDetailsForAssignTechnicianModel;
  isScheduleBoardHeaderActionLoading: boolean;
  showScheduleModal: boolean;
  selectedScheduleId: string | null;
  modalActiveKey: JobDetailsModalActiveKey;
  shouldPublishCrmEvent: boolean;
  compactView: boolean;
  setSelectedLocation: Dispatch<SetStateAction<LocationModel | null>>;
  setSelectedDate: Dispatch<SetStateAction<Date>>;
  setLaneConflicts: Dispatch<SetStateAction<ConflictLaneItemModel[] | null>>;
  setBoardData: Dispatch<
    SetStateAction<ScheduleBoardJobsAndBlockedSlotsListModel>
  >;
  setSearchTerm: Dispatch<SetStateAction<string>>;
  setServiceTypeFilter: Dispatch<SetStateAction<ServiceTypesRequestModel>>;
  setJobDetailsModalData: Dispatch<
    SetStateAction<ScheduleBoardJobModel | null>
  >;
  setStartDayHour: Dispatch<SetStateAction<number>>;
  setEndDayHour: Dispatch<SetStateAction<number>>;
  setTradingTimes: Dispatch<SetStateAction<TradingTimesModel>>;
  setIsNonTradingDate: Dispatch<SetStateAction<boolean>>;
  setSelectedTimeSlot: Dispatch<SetStateAction<Date | null>>;
  setSelectedLaneId: Dispatch<SetStateAction<string | null>>;
  resetCreateOrderFromScheduleBoard: () => void;
  setEditTechniciansModal: Dispatch<SetStateAction<OrderTechniciansModalType>>;
  setSelectedOrderDetailsForAssignTechnician: Dispatch<
    SetStateAction<SelectedOrderDetailsForAssignTechnicianModel>
  >;
  setIsScheduleBoardHeaderActionLoading: Dispatch<SetStateAction<boolean>>;
  setShowScheduleModal: Dispatch<SetStateAction<boolean>>;
  setSelectedScheduleId: Dispatch<SetStateAction<string | null>>;
  setModalActiveKey: Dispatch<SetStateAction<JobDetailsModalActiveKey>>;
  setShouldPublishCrmEvent: Dispatch<SetStateAction<boolean>>;
  setCompactView: Dispatch<SetStateAction<boolean>>;
  serviceLocationTimezone?: string;
};

const ScheduleBoardContext = createContext<ScheduleBoardContextProps | null>(
  null
);

const COMPACT_VIEW_STORAGE_KEY = 'scheduleBoard.compactView';

export const ScheduleBoardContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [selectedLocation, setSelectedLocation] =
    useState<LocationModel | null>(null);

  const serviceLocationTimezone =
    selectedLocation?.address?.timeZone ?? undefined;

  const [selectedDate, setSelectedDate] = useState<Date>(
    datetimeAtTimezone(serviceLocationTimezone).toDate()
  );

  const [laneConflicts, setLaneConflicts] = useState<
    ConflictLaneItemModel[] | null
  >(null);

  const [boardData, setBoardData] =
    useState<ScheduleBoardJobsAndBlockedSlotsListModel>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const [serviceTypeFilter, setServiceTypeFilter] =
    useState<ServiceTypesRequestModel>(serviceTypeEnum.enum.Static);
  const [jobDetailsModalData, setJobDetailsModalData] =
    useState<ScheduleBoardJobModel | null>(null);
  const [startDayHour, setStartDayHour] = useState<number>(7);
  const [endDayHour, setEndDayHour] = useState<number>(18);
  const [tradingTimes, setTradingTimes] = useState<TradingTimesModel>(null);
  const [isNonTradingDate, setIsNonTradingDate] = useState<boolean>(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<Date | null>(null);
  const [selectedLaneId, setSelectedLaneId] = useState<string | null>(null);
  const [editTechniciansModal, setEditTechniciansModal] =
    useState<OrderTechniciansModalType>({
      jobId: '',
      jobType: '',
      jobStartTime: '',
      jobEndTime: '',
      serviceLocationId: '',
      technicians: [],
      visible: false,
    });
  const [
    selectedOrderDetailsForAssignTechnician,
    setSelectedOrderDetailsForAssignTechnician,
  ] = useState<SelectedOrderDetailsForAssignTechnicianModel>({
    orderId: jobDetailsModalData?.orderId || '',
    jobId: jobDetailsModalData?.jobId || '',
  });
  // used for autoscroll to current time on today's date. only loading of board data after specified actions should trigger autoscroll.
  // set to true to trigger autoscroll on initial load
  const [
    isScheduleBoardHeaderActionLoading,
    setIsScheduleBoardHeaderActionLoading,
  ] = useState<boolean>(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(
    null
  );

  const [modalActiveKey, setModalActiveKey] =
    useState<JobDetailsModalActiveKey>('1');

  const [shouldPublishCrmEvent, setShouldPublishCrmEvent] = useState(false);

  const [compactView, setCompactViewState] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(COMPACT_VIEW_STORAGE_KEY);
      if (stored === null) return true;
      return stored === 'true';
    } catch {
      return true;
    }
  });

  const setCompactView: Dispatch<SetStateAction<boolean>> = (value) => {
    setCompactViewState((prev) => {
      const next = typeof value === 'function' ? (value as (p: boolean) => boolean)(prev) : value;
      try {
        localStorage.setItem(COMPACT_VIEW_STORAGE_KEY, String(next));
      } catch (error) {
        void error;
      }
      return next;
    });
  };

  const resetCreateOrderFromScheduleBoard = () => {
    setSelectedTimeSlot(null);
    setSelectedLaneId(null);
  };

  return (
    <ScheduleBoardContext.Provider
      value={{
        selectedLocation,
        selectedDate,
        laneConflicts,
        boardData,
        searchTerm,
        serviceTypeFilter,
        jobDetailsModalData,
        startDayHour,
        endDayHour,
        tradingTimes,
        isNonTradingDate,
        selectedTimeSlot,
        selectedLaneId,
        editTechniciansModal,
        selectedOrderDetailsForAssignTechnician,
        isScheduleBoardHeaderActionLoading,
        showScheduleModal,
        selectedScheduleId,
        modalActiveKey,
        shouldPublishCrmEvent,
        compactView,
        setCompactView,
        setSelectedLocation,
        setSelectedDate,
        setLaneConflicts,
        setBoardData,
        setSearchTerm,
        setServiceTypeFilter,
        setJobDetailsModalData,
        setStartDayHour,
        setEndDayHour,
        setTradingTimes,
        setIsNonTradingDate,
        setSelectedTimeSlot,
        setSelectedLaneId,
        resetCreateOrderFromScheduleBoard,
        setEditTechniciansModal,
        setSelectedOrderDetailsForAssignTechnician,
        setIsScheduleBoardHeaderActionLoading,
        setShowScheduleModal,
        setSelectedScheduleId,
        setModalActiveKey,
        setShouldPublishCrmEvent,
        serviceLocationTimezone,
      }}
    >
      {children}
    </ScheduleBoardContext.Provider>
  );
};

export const useScheduleBoardContext = () => {
  const context = useContext(ScheduleBoardContext);
  if (!context) {
    throw new Error(
      'useScheduleBoardContext must be used within a ScheduleBoardContextProvider'
    );
  }
  return context;
};
